import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

const STATUS_LABELS = {
  pending_payment: { label: "PAGTO PENDENTE", cls: "" },
  pending_confirmation: { label: "A CONFIRMAR", cls: "" },
  confirmed: { label: "CONFIRMADA", cls: "cp-pill--ok" },
  assigned: { label: "MOTORISTA ATRIBUÍDO", cls: "cp-pill--ok" },
  in_progress: { label: "EM CURSO", cls: "cp-pill--ok" },
  completed: { label: "CONCLUÍDA", cls: "cp-pill--ok" },
  cancelled: { label: "CANCELADA", cls: "cp-pill--warn" },
  refund_pending: { label: "REEMBOLSO PENDENTE", cls: "" },
  refunded: { label: "REEMBOLSADA", cls: "" },
};

export default function ClientBookings() {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const filter = params.get("filter"); // "past" | null
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    api.get("/client/bookings").then((r) => setBookings(r.data || [])).catch(() => setBookings([]));
  }, []);

  const locale = lang.toLowerCase() === "pt" ? "pt-PT" : lang.toLowerCase();

  const filtered = useMemo(() => {
    if (filter !== "past") return bookings;
    const now = new Date();
    return bookings.filter((b) => {
      const dt = new Date(b.service_datetime);
      return dt < now || ["completed", "cancelled", "refunded"].includes(b.status);
    });
  }, [bookings, filter]);

  const title = filter === "past" ? "Histórico" : "As minhas reservas";
  const desc = filter === "past"
    ? "Reservas passadas e concluídas — o seu registo completo com a DiTours."
    : "Todas as suas reservas com a DiTours num só sítio. Filtre, acompanhe estados e imprima recibos.";

  return (
    <div data-testid="client-bookings-page">
      <div className="cp-page-head">
        <span className="cp-eyebrow">{filter === "past" ? "Registo" : "Reservas"}</span>
        <h1 className="cp-title">{title}</h1>
        <p className="cp-desc">{desc}</p>
      </div>

      <div className="cp-card cp-card--flat">
        <table className="cp-table">
          <thead>
            <tr>
              <th>Ref.</th>
              <th>Data</th>
              <th>Tipo</th>
              <th>Trajecto</th>
              <th style={{ textAlign: "right" }}>Total</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6}><div className="cp-empty">Sem reservas para mostrar.</div></td></tr>
            )}
            {filtered.map((b) => {
              const st = STATUS_LABELS[b.status] || { label: (b.status || "").toUpperCase(), cls: "" };
              return (
                <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                  <td><span className="cp-table__ref">{b.reference}</span></td>
                  <td>{new Date(b.service_datetime).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td style={{ textTransform: "uppercase", fontSize: "0.75rem", color: "#94a3b8", letterSpacing: "0.08em" }}>{b.type}</td>
                  <td style={{ maxWidth: 300, fontSize: "0.82rem", color: "#94a3b8" }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.pickup_address}</div>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>→ {b.destination_address}</div>
                  </td>
                  <td className="cp-table__amount">€{(b.total_amount || 0).toFixed(2)}</td>
                  <td>
                    <span className={`cp-pill ${st.cls}`} data-testid={`booking-status-${b.id}`}>{st.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
